import React, { PureComponent } from "react";
import moment from "moment";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { XRayTooltip } from "../Utils/XrayUtils.js";

import {
  Metric,
  StandardTooltip,
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  CustomizedYAxisTickRightCurrency,
  selectFormat,
  Error,
  MAIN_COLORS,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import { getXrayTooltipPosition } from "helpers/DOM";

const widgetConfig = {
  total_spend: {
    label: "Total Spend",
    type: "currency",
  },
  revenue: {
    label: "Ad Network Revenue",
    type: "currency",
  },
  roas: {
    label: "ROAS",
    type: "number",
    format: "0.00",
  },
};

class RenderMetric extends PureComponent {
  render() {
    const { data, type, label, format, size } = this.props;
    const columnClass = "col-6 col-md-3 mb-3";

    return (
      <div className={columnClass}>
        <h6 className="widget__metric-label">{label}</h6>
        <Metric {...data} size={size} type={type} format={format} />
      </div>
    );
  }
}

class GothamROAS extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();

    this.state = {
      widgetsData: props.data?.data || {},
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();
    if (!this.chartRef || !this.chartRef.state) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  getExportData = () => {
    const { data } = this.props;

    if (!data?.data?.length) {
      return {
        data: [],
        opts: { type: "other" },
      };
    }

    const chartData = data.data.map((point) => {
      return {
        Date: point.date,
        "Total Spend": point.total_spend || 0,
        "Ad Network Revenue": point.revenue || 0,
        ROAS: point.roas || 0,
      };
    });

    return {
      data: chartData,
      opts: {
        type: "other",
      },
    };
  };

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  renderMetrics = (data) => {
    if (!data || !data.metrics) return null;

    const metrics = data.metrics;

    return Object.keys(widgetConfig).map((metric, key) => {
      if (!metrics[metric]) return null;

      const metricData = {
        value: metrics[metric].value,
        delta: metrics[metric].change * 100,
        delta_type:
          metrics[metric].direction === "up" ? "positive" : "negative",
      };

      return (
        <RenderMetric
          key={metric}
          data={metricData}
          type={widgetConfig[metric].type}
          label={widgetConfig[metric].label}
          format={widgetConfig[metric].format}
          size="md"
        />
      );
    });
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey, date: d.date },
      });
    }
  }

  render() {
    const { data } = this.props;

    if (!data || !data.data || !Array.isArray(data.data)) {
      return <Error minHeight={400} message="No data available" />;
    }

    const chartData = data.data;

    return (
      <>
        <div key="metrics" className="row mb-4">
          {this.renderMetrics(data)}
        </div>
        <div className="row" ref={this.divPosition}>
          <ResponsiveContainer
            className="legends-non-interactive"
            height={400}
            width="100%"
            ref={(ref) => (this.chartRef = ref)}
          >
            <ComposedChart
              data={chartData}
              margin={{ top: 12, right: 0, bottom: 0, left: 0 }}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                if (d === null) {
                  this.closeTooltip();
                  return;
                }

                const dataPoint = chartData.find(
                  (point) => point.date === d.activeLabel
                );

                if (!dataPoint) {
                  console.error(
                    "No data point found for active label:",
                    d.activeLabel
                  );
                  return;
                }

                // Format the date
                const formattedDate = moment(dataPoint.date).format(
                  "MMMM DD, YYYY"
                );

                // Structure the data as an array of metrics
                const extra = [
                  {
                    key: "Total Spend",
                    value: dataPoint.total_spend,
                    color: MAIN_COLORS[0],
                  },
                  {
                    key: "Ad Network Revenue",
                    value: dataPoint.revenue,
                    color: MAIN_COLORS[1],
                  },
                  {
                    key: "ROAS",
                    value: dataPoint.roas,
                    color: MAIN_COLORS[2],
                  },
                ];

                const activePayload = [
                  {
                    payload: {
                      name: "Metrics",
                      value: dataPoint.total_spend + dataPoint.revenue,
                      idx: 0,
                    },
                  },
                ];

                this.setState({
                  tooltip: {
                    ...d,
                    extra,
                    show: true,
                    activeCoordinate: d.activeCoordinate,
                    activePayload,
                    activeLabel: formattedDate,
                    date: formattedDate,
                  },
                });
              }}
            >
              <XAxis
                dataKey="date"
                interval="preserveStartEnd"
                scale="point"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
                height={40}
              />

              <YAxis
                yAxisId="left"
                orientation="left"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTickRightCurrency />}
                domain={[0, "auto"]}
                width={60}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedYAxisTick />}
                domain={[0, "auto"]}
                width={60}
              />

              <Bar
                yAxisId="left"
                dataKey="total_spend"
                fill={MAIN_COLORS[0]}
                barSize={20}
                name="Total Spend"
              />

              <Bar
                yAxisId="left"
                dataKey="revenue"
                fill={MAIN_COLORS[1]}
                barSize={20}
                name="Ad Network Revenue"
              />

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="roas"
                stroke={MAIN_COLORS[2]}
                strokeWidth={2}
                dot={false}
                name="ROAS"
              />

              <Legend verticalAlign="bottom" height={36} />

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data && tooltip_data.total_spend && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: MAIN_COLORS[0],
                                        }}
                                      ></span>
                                      Total Spend
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "currency",
                                        tooltip_data.total_spend
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.revenue && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: MAIN_COLORS[1],
                                        }}
                                      ></span>
                                      Ad Network Revenue
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "currency",
                                        tooltip_data.revenue
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.roas && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: MAIN_COLORS[2],
                                        }}
                                      ></span>
                                      ROAS
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {Number(tooltip_data.roas).toFixed(2)}
                                    </span>
                                  </div>
                                )}

                                {this.state.widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={this.state.widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: `${this.state.tooltip.activeCoordinate.y}px`,
                left:
                  this.state.tooltip.activeCoordinate.x + 220 < this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
                position: "absolute",
                zIndex: 1000,
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                close={this.closeTooltip.bind(this)}
                insights={this.state.widgetsInsights}
              />
            </div>
          ) : null}

          <div className="featured-insight-tooltip" ref={this.node}>
            {this.state.widgetsInsights.length > 0 &&
              !this.state.tooltip.show && (
                <RenderTooltipInsights data={this.state.widgetsInsights} />
              )}
          </div>
        </div>
      </>
    );
  }
}

export default GothamROAS;
